import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LayoutService } from 'src/app/services/layout.service';
import { OverlayService, OverlayType } from 'src/app/services/overlay.service';
import { VisualAnalysisService } from 'src/app/services/visualanalysis.service';
import { ProductStatus } from 'src/app/models/layout/product-status';

interface LegendItem {
  color: string;
  text: string;
  colorIndex: number; 
}

@Component({
    selector: 'app-overlay-legend',
    templateUrl: './overlay-legend.component.html',
    styleUrl: './overlay-legend.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class OverlayLegendComponent implements OnInit {
  overlayType: OverlayType = OverlayType.NONE;
  overlayTypeEnum = OverlayType; // Expose the OverlayType enum
  legendItems: LegendItem[] = [];

  private destroyRef = inject(DestroyRef);

  constructor(
    private layoutService: LayoutService,
    private overlayService: OverlayService,
    private visualAnalysisService: VisualAnalysisService
  ) {}
  ngOnInit(): void {
    this.overlayService.overlayType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((overlayType) => {
      this.overlayType = overlayType;
      if (this.overlayType != OverlayType.NONE) this.updateLegendItems();
    });
    // Visual Analysis colours products through its own custom colour map, so
    // rebuild the legend whenever that map changes (recolour on hierarchy /
    // metric switch, or a brand/segment visibility toggle).
    this.visualAnalysisService.customColorMap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.overlayType === OverlayType.VISUAL_ANALYSIS) {
          this.updateLegendItems();
        }
      });
  }

  onMouseEnter(item: LegendItem) {
    // Visual Analysis items use custom colours (colorIndex -1) that the palette-
    // based highlight can't address, so skip highlighting for them.
    if (item.colorIndex < 0) return;
    this.overlayService.setHighlightedOverlayIndex(item.colorIndex);
  }

  onMouseLeave() {
    this.overlayService.setHighlightedOverlayIndex(null);
  }

  private updateLegendItems(): void {
    switch (this.overlayType) {
      case OverlayType.NONE:
        this.legendItems = [];
        break;
      case OverlayType.VISUAL_ANALYSIS:
        this.legendItems = this.generateVisualAnalysisLegendItems();
        break;
      case OverlayType.DOS:
        this.legendItems = this.generateDOSLegendItems();
        break;
      case OverlayType.CPI_CATEGORY:
        this.legendItems = this.generateCPICategoryLegendItems();
        break;
      case OverlayType.CPI_SEGMENT:
        this.legendItems = this.generateCPISegmentLegendItems();
        break;
      case OverlayType.SEGMENT:
        this.legendItems = this.generateSegmentLegendItems();
        break;
      case OverlayType.SEGMENT_FAMILY:
        this.legendItems = this.generateSegmentFamilyLegendItems();
        break;
      case OverlayType.RSP:
        this.legendItems = this.generateRSPLegendItems();
        break;
      case OverlayType.LIFECYCLE_STATUS:
        this.legendItems = this.generateLifecycleStatusLegendItems();
        break;
      case OverlayType.LIFECYCLE_RANGE:
        this.legendItems = this.generateLifecycleRangeLegendItems();
        break;
      case OverlayType.LIFECYCLE_PLAN:
        this.legendItems = this.generateLifecyclePlanLegendItems();
        break;
      case OverlayType.CASE_PACK:
        this.legendItems = this.generateCasePackLegendItems();
        break;
    }
  }

  private generateCasePackLegendItems(): LegendItem[] {
    return [
      { color: 'red', text: '< case pack', colorIndex: this.overlayService.getColorIndex('red') },
      { color: 'green', text: '>= case pack', colorIndex: this.overlayService.getColorIndex('green') },
    ];
  }

  private generateDOSLegendItems(): LegendItem[] {
    return [
      { color: 'red', text: 'DOS under minimum', colorIndex: this.overlayService.getColorIndex('red') },
      { color: 'green', text: 'DOS in range', colorIndex: this.overlayService.getColorIndex('green') },
      { color: 'orange', text: 'DOS over maximum', colorIndex: this.overlayService.getColorIndex('orange') },
      { color: 'blue', text: 'DOS unknown / No sales', colorIndex: this.overlayService.getColorIndex('blue') },
    ];
  }

  private generateCPICategoryLegendItems(): LegendItem[] {
    const thresholds = this.overlayService.getCPIThresholds();
    
    return [
      { color: 'red', text: `CPI ≤ ${thresholds.tier1}`, colorIndex: this.overlayService.getColorIndex('red') },
      { color: 'orange', text: `CPI ≤ ${thresholds.tier2}`, colorIndex: this.overlayService.getColorIndex('orange') },
      { color: 'yellow', text: `CPI ≤ ${thresholds.tier3}`, colorIndex: this.overlayService.getColorIndex('yellow') },
      { color: 'green', text: `CPI > ${thresholds.tier3}`, colorIndex: this.overlayService.getColorIndex('green') }
    ];
  }

  private generateCPISegmentLegendItems(): LegendItem[] {
    const thresholds = this.overlayService.getCPIThresholds();
    
    return [
      { color: 'red', text: `CPI ≤ ${thresholds.tier1}`, colorIndex: this.overlayService.getColorIndex('red') },
      { color: 'orange', text: `CPI ≤ ${thresholds.tier2}`, colorIndex: this.overlayService.getColorIndex('orange') },
      { color: 'yellow', text: `CPI ≤ ${thresholds.tier3}`, colorIndex: this.overlayService.getColorIndex('yellow') },
      { color: 'green', text: `CPI > ${thresholds.tier3}`, colorIndex: this.overlayService.getColorIndex('green') }
    ];
  }

  private generateRSPLegendItems(): LegendItem[] {
    const boundaries = this.overlayService.getRSPBoundaries();

    if (!boundaries) {
      return [];
    }

    return [
      { color: 'blue', text: 'No RSP', colorIndex: this.overlayService.getColorIndex('blue')},
      { color: 'green', text: `1 - ${boundaries.q1.toFixed(0)}`, colorIndex: this.overlayService.getColorIndex('green') },
      {
        color: 'yellow',
        text: `${boundaries.q1.toFixed(0)} - ${boundaries.q2.toFixed(0)}`,
        colorIndex: this.overlayService.getColorIndex('yellow')
      },
      {
        color: 'orange',
        text: `${boundaries.q2.toFixed(0)} - ${boundaries.q3.toFixed(0)}`,
        colorIndex: this.overlayService.getColorIndex('orange')
      },
      { color: 'red', text: `${boundaries.q3.toFixed(0)}+`, colorIndex: this.overlayService.getColorIndex('red') },
    ];
  }

  private generateVisualAnalysisLegendItems(): LegendItem[] {
    // The custom colour map only holds currently-coloured (visible) products,
    // keyed per brand. Collapse it to one entry per colour, labelled by brand.
    const colorMap = this.visualAnalysisService.getCustomColors();
    const byBrand = new Map<string, string>();

    colorMap.forEach((color, productId) => {
      const reference = this.layoutService.planogramItems.get(productId);
      if (reference && !byBrand.has(reference.brand)) {
        byBrand.set(reference.brand, color);
      }
    });

    return Array.from(byBrand.entries())
      .map(([text, color]) => ({ color, text, colorIndex: -1 }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  private generateSegmentLegendItems(): LegendItem[] {
    let segments: string[] = [];

    let layout = this.layoutService.getCurrentLayout();

    layout?.drops.forEach((drop) => {
      drop.shelves.forEach((shelf) => {
        shelf.stacks.forEach((stack) => {
          stack.products.forEach((product) => {
            let reference = this.layoutService.planogramItems.get(
              product.productId
            );
            if (reference && segments.indexOf(reference.categoryLevel2) == -1)
              segments.push(reference.categoryLevel2);
          });
        });
      });
    });

    let items: LegendItem[] = [];

    for (let i = 0; i < segments.length; i++) {
      items.push({
        color: this.overlayService.overlayColors[i],
        text: segments[i],
        colorIndex: i
      });
    }

    return items;
  }

  private generateLifecycleStatusLegendItems(): LegendItem[] {
    return [
      { color: 'cyan', text: 'NPI', colorIndex: this.overlayService.getColorIndex('cyan') },
      { color: 'green', text: 'Active', colorIndex: this.overlayService.getColorIndex('green') },
      { color: 'goldenrod', text: 'To Be Discontinued', colorIndex: this.overlayService.getColorIndex('goldenrod') },
      { color: 'saddlebrown', text: 'Discontinued', colorIndex: this.overlayService.getColorIndex('saddlebrown') },
    ];
  }

  private generateLifecycleRangeLegendItems(): LegendItem[] {
    return [
      { color: 'green', text: 'Ranged', colorIndex: this.overlayService.getColorIndex('green') },
      { color: 'steelblue', text: 'Not Ranged', colorIndex: this.overlayService.getColorIndex('steelblue') },
    ];
  }

  private generateLifecyclePlanLegendItems(): LegendItem[] {
    return [
      { color: 'limegreen', text: 'Adding to Range', colorIndex: this.overlayService.getColorIndex('limegreen') },
      { color: 'orangered', text: 'Removing from Range', colorIndex: this.overlayService.getColorIndex('orangered') },
    ];
  }

  private generateSegmentFamilyLegendItems(): LegendItem[] {
    let segment_families: string[] = [];

    let layout = this.layoutService.getCurrentLayout();

    layout?.drops.forEach((drop) => {
      drop.shelves.forEach((shelf) => {
        shelf.stacks.forEach((stack) => {
          stack.products.forEach((product) => {
            let reference = this.layoutService.planogramItems.get(
              product.productId
            );
            if(reference){
              let segment_family = `${reference.categoryLevel2} - ${reference.categoryLevel3}`;
            if (segment_families.indexOf(segment_family) == -1)
              segment_families.push(segment_family);
            }
          });
        })
      });
    });

    let items: LegendItem[] = [];

    for (let i = 0; i < segment_families.length; i++) {
      items.push({
        color: this.overlayService.overlayColors[i],
        text: segment_families[i],
        colorIndex: i
      });
    }

    return items;
  }

}
